import { useEffect, useState } from 'react'
import GeoInOutNotification from './GeoInOutNotification'

const COOKIE_KEY = 'geo_inout_data'
// const IN_MESSAGE_DELAY = 10 * 1000 // 10秒（デバッグ用）
// const OUT_MESSAGE_DELAY = 30 * 1000 // 30秒（デバッグ用）
// 本番環境では以下を使用
const IN_MESSAGE_DELAY = 5 * 60 * 1000 // 5分
const OUT_MESSAGE_DELAY = 60 * 60 * 1000 // 1時間

// メッセージ定義
const IN_MESSAGE =
  '【三国湊 散策スタート！】 ご到着ありがとうございます。まずは「旧森田銀行」や「旧岸名家」を巡るのがおすすめです。 素敵なお写真が撮れたら、ぜひ #三国湊 でシェアしてくださいね。'
const OUT_MESSAGE =
  '本日は三国湊にお越しいただきありがとうございました。お気をつけてお帰りください。また違う季節の三国湊でお会いできるのを楽しみにしております。'

type GeoInOutState = 'START' | 'IN' | 'OUT'

interface GeoInOutData {
  state: GeoInOutState
  firstAccessTime: string | null
  inMessageShownTime: string | null
  outMessageShownTime: string | null
  inMessageDismissed: boolean
  outMessageDismissed: boolean
}

/**
 * エリア入退出管理を行うコンポーネント（Redux不使用）
 */
export default function GeoInOutManager() {
  const [geoInOutData, setGeoInOutData] = useState<GeoInOutData>({
    state: 'START',
    firstAccessTime: null,
    inMessageShownTime: null,
    outMessageShownTime: null,
    inMessageDismissed: false,
    outMessageDismissed: false,
  })
  const [showNotification, setShowNotification] = useState(false)
  const [notificationType, setNotificationType] = useState<'in' | 'out'>('in')
  const [notificationMessage, setNotificationMessage] = useState('')

  // Cookieから読み込み
  const loadFromCookie = () => {
    if (typeof document === 'undefined') return null

    const savedData = document.cookie.split('; ').find((row) => row.startsWith(`${COOKIE_KEY}=`))

    if (savedData) {
      try {
        const data = JSON.parse(decodeURIComponent(savedData.split('=')[1]))
        return data as GeoInOutData
      } catch (e) {
        return null
      }
    }
    return null
  }

  // Cookieに保存
  const saveToCookie = (data: GeoInOutData) => {
    if (typeof document === 'undefined') return

    const expires = new Date()
    expires.setFullYear(expires.getFullYear() + 1)
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(
      JSON.stringify(data),
    )}; expires=${expires.toUTCString()}; path=/`
  }

  // 初期化
  useEffect(() => {
    const savedData = loadFromCookie()

    if (savedData) {
      console.log('[GeoInOutManager] Loaded from cookie:', savedData)
      setGeoInOutData(savedData)
    } else {
      const now = new Date().toISOString()
      const initialData: GeoInOutData = {
        state: 'START',
        firstAccessTime: now,
        inMessageShownTime: null,
        outMessageShownTime: null,
        inMessageDismissed: false,
        outMessageDismissed: false,
      }
      console.log('[GeoInOutManager] Initializing new data:', initialData)
      setGeoInOutData(initialData)
      saveToCookie(initialData)
    }
  }, [])

  // Cookieへの保存
  useEffect(() => {
    if (geoInOutData.firstAccessTime) {
      saveToCookie(geoInOutData)
    }
  }, [geoInOutData])

  // メッセージ表示のタイミング制御
  useEffect(() => {
    if (!geoInOutData.firstAccessTime) return

    const firstAccessTime = new Date(geoInOutData.firstAccessTime).getTime()
    const now = Date.now()

    console.log('[GeoInOutManager] Current state:', geoInOutData.state)
    console.log('[GeoInOutManager] Time since first access:', (now - firstAccessTime) / 1000, 'seconds')

    if (geoInOutData.state === 'START') {
      const timeSinceFirstAccess = now - firstAccessTime

      if (timeSinceFirstAccess >= IN_MESSAGE_DELAY) {
        console.log('[GeoInOutManager] Showing IN message immediately')
        const newData: GeoInOutData = {
          ...geoInOutData,
          state: 'IN',
          inMessageShownTime: new Date().toISOString(),
        }
        setGeoInOutData(newData)
        if (!newData.inMessageDismissed) {
          setNotificationType('in')
          setNotificationMessage(IN_MESSAGE)
          setShowNotification(true)
        }
      } else {
        const delay = IN_MESSAGE_DELAY - timeSinceFirstAccess
        console.log('[GeoInOutManager] Setting timeout for IN message in', delay / 1000, 'seconds')
        const timeout = setTimeout(() => {
          console.log('[GeoInOutManager] Timeout triggered - Showing IN message')
          const newData: GeoInOutData = {
            ...geoInOutData,
            state: 'IN',
            inMessageShownTime: new Date().toISOString(),
          }
          setGeoInOutData(newData)
          if (!newData.inMessageDismissed) {
            setNotificationType('in')
            setNotificationMessage(IN_MESSAGE)
            setShowNotification(true)
          }
        }, delay)
        return () => clearTimeout(timeout)
      }
    } else if (geoInOutData.state === 'IN') {
      if (!geoInOutData.inMessageShownTime) return

      if (!geoInOutData.inMessageDismissed) {
        setNotificationType('in')
        setNotificationMessage(IN_MESSAGE)
        setShowNotification(true)
        return
      }

      const inMessageTime = new Date(geoInOutData.inMessageShownTime).getTime()
      const timeSinceInMessage = now - inMessageTime

      console.log('[GeoInOutManager] Time since IN message:', timeSinceInMessage / 1000, 'seconds')

      if (timeSinceInMessage >= OUT_MESSAGE_DELAY) {
        console.log('[GeoInOutManager] Showing OUT message immediately')
        const newData: GeoInOutData = {
          ...geoInOutData,
          state: 'OUT',
          outMessageShownTime: new Date().toISOString(),
        }
        setGeoInOutData(newData)
        if (!newData.outMessageDismissed) {
          setNotificationType('out')
          setNotificationMessage(OUT_MESSAGE)
          setShowNotification(true)
        }
      } else {
        const delay = OUT_MESSAGE_DELAY - timeSinceInMessage
        console.log('[GeoInOutManager] Setting timeout for OUT message in', delay / 1000, 'seconds')
        const timeout = setTimeout(() => {
          console.log('[GeoInOutManager] Timeout triggered - Showing OUT message')
          const newData: GeoInOutData = {
            ...geoInOutData,
            state: 'OUT',
            outMessageShownTime: new Date().toISOString(),
          }
          setGeoInOutData(newData)
          if (!newData.outMessageDismissed) {
            setNotificationType('out')
            setNotificationMessage(OUT_MESSAGE)
            setShowNotification(true)
          }
        }, delay)
        return () => clearTimeout(timeout)
      }
    } else if (geoInOutData.state === 'OUT' && !geoInOutData.outMessageDismissed) {
      setNotificationType('out')
      setNotificationMessage(OUT_MESSAGE)
      setShowNotification(true)
    }
  }, [geoInOutData])

  if (!showNotification) return null

  const handleClose = () => {
    const updatedData: GeoInOutData = {
      ...geoInOutData,
      ...(notificationType === 'in' ? { inMessageDismissed: true } : { outMessageDismissed: true }),
    }
    setGeoInOutData(updatedData)
    saveToCookie(updatedData)
    setShowNotification(false)
  }

  return <GeoInOutNotification type={notificationType} message={notificationMessage} onClose={handleClose} />
}
